import { useState, type FormEvent } from "react";

import { ApiError, type Me, type Recipient, type SuppressionReason } from "../lib/api";
import { formatDate, plural } from "../lib/format";
import { useAddRecipient, useReactivateRecipient, useRecipients, useRemoveRecipient, useResendRecipient } from "../lib/queries";
import { ConfirmDialog } from "./Dialog";
import { Loading } from "./Loading";
import { useToast } from "./Toast";

export const SUPPRESSION_LABELS: Record<SuppressionReason, string> = {
  hard_bounce: "adresse inexistante",
  invalid_email: "adresse invalide",
  blocked: "refusé par le serveur",
  spam: "signalé comme spam",
  unsubscribed: "désinscrit",
};

function StatusChip({ recipient }: { recipient: Recipient }) {
  if (recipient.status === "suppressed" && recipient.suppression) {
    return <span className="chip chip--danger">Bloqué : {SUPPRESSION_LABELS[recipient.suppression.reason]}</span>;
  }
  if (recipient.status === "pending") return <span className="chip">En attente de confirmation</span>;
  return <span className="chip chip--active">Actif</span>;
}

function statusNote(recipient: Recipient): string {
  const { suppression } = recipient;
  if (recipient.status === "suppressed" && suppression) {
    const since = formatDate(suppression.at);
    if (suppression.can_retry) {
      return `Nos alertes n'arrivent plus à cette adresse depuis le ${since}. Une fois la boîte aux lettres corrigée, cliquez sur « Réessayer ».`;
    }
    return suppression.reason === "spam"
      ? `Un de nos e-mails a été signalé comme spam depuis cette adresse le ${since} : nous ne lui envoyons plus d'alerte. Seul un administrateur peut lever ce blocage.`
      : `Cette adresse s'est désinscrite le ${since}. Seul un administrateur peut la réactiver.`;
  }
  if (recipient.is_account_email) return "Adresse de votre compte, toujours destinataire.";
  if (recipient.status === "pending") return `Invitation envoyée le ${formatDate(recipient.created_at)}. Aucune alerte avant sa confirmation.`;
  return `Confirmée le ${formatDate(recipient.confirmed_at)}.`;
}

export function AlertRecipients({ me }: { me: Me }) {
  const recipients = useRecipients();
  const add = useAddRecipient();
  const remove = useRemoveRecipient();
  const resend = useResendRecipient();
  const reactivate = useReactivateRecipient();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [removing, setRemoving] = useState<Recipient | null>(null);

  if (recipients.isPending) return <Loading label="Chargement des destinataires" />;
  if (recipients.isError) return <div className="notice notice--error">{recipients.error.message}</div>;

  const extras = recipients.data.filter((r) => !r.is_account_email).length;
  const limitReached = extras >= me.max_alert_recipients;

  function submit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    add.mutate(value, {
      onSuccess: (recipient) => {
        setEmail("");
        toast(`Un e-mail de confirmation a été envoyé à ${recipient.email}.`);
      },
    });
  }

  return (
    <div className="recipients">
      {me.alert_suppressed && (
        <div className="notice notice--error">
          <span>
            Nos alertes n'arrivent plus à <strong>{me.email}</strong>. Le motif et la marche à suivre sont indiqués
            ci-dessous.
          </span>
        </div>
      )}

      <p>
        Vos alertes peuvent aussi partir vers des collègues. Chaque adresse ajoutée reçoit d'abord un e-mail de
        confirmation : elle ne reçoit rien tant qu'elle n'a pas accepté, et peut se retirer à tout moment.
      </p>

      <ul className="recipient-list" aria-label="Destinataires des alertes">
        {recipients.data.map((recipient) => (
          <li key={recipient.id ?? "account"} className="recipient">
            <div className="recipient__main">
              <span className="recipient__email">{recipient.email}</span>
              <StatusChip recipient={recipient} />
              <span className="recipient__note">{statusNote(recipient)}</span>
            </div>
            <div className="recipient__actions">
              {recipient.suppression?.can_retry && (
                <button
                  type="button"
                  className="button button--quiet button--small"
                  disabled={reactivate.isPending}
                  aria-label={`Réessayer ${recipient.email}`}
                  onClick={() =>
                    reactivate.mutate(recipient.email, {
                      onSuccess: () => toast(`Les alertes seront de nouveau envoyées à ${recipient.email}.`),
                      onError: (error) => toast(error.message, "error"),
                    })
                  }
                >
                  Réessayer
                </button>
              )}
              {recipient.id !== null && recipient.status === "pending" && (
                <button
                  type="button"
                  className="button button--quiet button--small"
                  disabled={resend.isPending}
                  aria-label={`Renvoyer la confirmation à ${recipient.email}`}
                  onClick={() =>
                    resend.mutate(
                      { id: recipient.id!, email: recipient.email },
                      {
                        onSuccess: (data) => toast(data.message),
                        onError: (error) => toast(error.message, "error"),
                      },
                    )
                  }
                >
                  Renvoyer la confirmation
                </button>
              )}
              {recipient.id !== null && (
                <button
                  type="button"
                  className="button button--quiet button--small"
                  aria-label={`Retirer ${recipient.email}`}
                  onClick={() => setRemoving(recipient)}
                >
                  Retirer
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <form className="recipient-add" onSubmit={submit}>
        <div className="field">
          <label className="field__label" htmlFor="recipient-email">
            Ajouter une adresse
          </label>
          <div className="recipient-add__row">
            <input
              id="recipient-email"
              className="input"
              type="email"
              autoComplete="off"
              required
              maxLength={320}
              placeholder="prenom.nom@exemple.fr"
              value={email}
              disabled={limitReached}
              aria-invalid={add.isError || undefined}
              aria-describedby={add.isError ? "recipient-error recipient-limit" : "recipient-limit"}
              onChange={(e) => {
                setEmail(e.target.value);
                if (add.isError) add.reset();
              }}
            />
            <button type="submit" className="button" disabled={add.isPending || limitReached}>
              {add.isPending ? "Envoi en cours" : "Ajouter"}
            </button>
          </div>
          <span id="recipient-limit" className="field__hint">
            {plural(extras, "adresse supplémentaire", "adresses supplémentaires")} sur {me.max_alert_recipients} au
            maximum.{limitReached && " Retirez une adresse pour en ajouter une autre."}
          </span>
        </div>
        {add.isError && (
          <div id="recipient-error" className="notice notice--error" role="alert">
            {add.error.message}
          </div>
        )}
      </form>

      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title="Retirer ce destinataire ?"
        body={`Les alertes ne seront plus envoyées à ${removing?.email ?? ""}. Pour l'ajouter à nouveau, l'adresse devra confirmer une nouvelle fois.`}
        confirmLabel="Retirer le destinataire"
        danger
        pending={remove.isPending}
        onConfirm={() => {
          if (removing?.id == null) return;
          const target = { id: removing.id, email: removing.email };
          remove.mutate(target, {
            onSuccess: () => {
              setRemoving(null);
              toast(`${target.email} ne recevra plus vos alertes.`);
            },
            onError: (error) => {
              // 404: the address already removed itself; the list is refreshed.
              if (error instanceof ApiError && error.status === 404) setRemoving(null);
              toast(error.message, "error");
            },
          });
        }}
      />
    </div>
  );
}
